// src/review/steps/RiskReviewStep.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Stack, Grid, Alert, Button } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useReview } from '../context/ReviewContext';
import { updateReviewProgress } from '../../redux/actions/reviewActions';
// RTK async thunks
import { fetchRiskCardData } from '../../redux/slices/assessmentsSlice';
import {
  fetchAssessmentRisks,
  fetchBuilderRisks,
  fetchExplorerRisks,
  addManualRisk,
} from '../../redux/slices/risksSlice';
import { setSavedControls, fetchSavedControlEvaluations } from '../../redux/actions/controlActions';
import { fetchResidualRisk } from '../../redux/actions/residualRiskActions';
// Removed selectRisksStateBridge - using direct slice access to avoid infinite re-renders
import { transformRisksToFlatList, groupRisksByArea } from '../utils/dataTransformers';
import RiskNavigator from '../canvas/navigation/RiskNavigator';
import RiskCanvas from '../canvas/RiskCanvas';

/**
 * RiskReviewStep - Step 1 of review flow
 * Shows all safety & alignment risks with navigation and canvas
 */
function RiskReviewStep() {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid, assessmentType, currentRiskId, selectRisk, goToStep } = useReview();

  // After risks, builder reviews continue to security; navigator/explorer skip
  // straight to summary (security is owned by the parent Builder review).
  const nextStepAfterRisks = (assessmentType === 'navigator' || assessmentType === 'explorer')
    ? 'summary'
    : 'security';

  // Pick the right slice key for this assessment type. Risks are stored under
  // different keys per type but share the same {areaKey: {areaRisks: {category: Risk[]}}} shape.
  const riskFormDataKey = assessmentType === 'navigator'
    ? 'builderRisks'
    : assessmentType === 'explorer'
    ? 'explorerRisks'
    : 'modelRisks';

  // Get risk form data from Redux using direct slice access (stable references)
  // Reading directly from riskItems slice avoids the infinite re-render issue
  // caused by the selector factory pattern in selectRisksStateBridge
  const riskFormData = useSelector(
    (state) => state.riskItems?.byAssessment?.[assessmentUuid]?.[riskFormDataKey] || {}
  );

  // Get additional data from separate Redux slices
  const savedControls = useSelector((state) => state.controls.savedControls || {});

  // RTK-first: read from RTK slice, fall back to legacy
  // Using shallowEqual to prevent re-renders when merged object has same values
  const residualRiskData = useSelector(
    (state) => {
      const rtkData = state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {};
      const legacyData = state.residualRisk?.byModel?.[assessmentUuid] || {};
      return { ...legacyData, ...rtkData };
    },
    shallowEqual
  );

  // Get template for this assessment (for add risk modal categories)
  const templateData = useSelector((state) => {
    const assessment = state.assessments?.byId?.[assessmentUuid];
    const templateId = assessment?.template_id;
    return templateId ? state.templates?.byId?.[templateId] : null;
  });

  // Transform to flat list and enrich with additional data
  const allRisks = useMemo(() => {
    const flatRisks = transformRisksToFlatList(riskFormData);

    // Enrich each risk with controls and residual risk data
    return flatRisks.map(risk => ({
      ...risk,
      // Add controls for this risk
      controls: savedControls[risk.riskId] || [],
      // Add residual risk data for this risk
      residualRisk: residualRiskData[risk.riskId] || null,
      // evaluation is already on the risk object from riskFormData as evaluationData
      evaluation: risk.evaluationData || null,
    }));
  }, [riskFormData, savedControls, residualRiskData]);

  const risksByArea = useMemo(() =>
    groupRisksByArea(allRisks),
    [allRisks]
  );

  // Current risk being viewed
  const [currentRiskIndex, setCurrentRiskIndex] = useState(0);
  const currentRisk = allRisks[currentRiskIndex];

  // Fetch all required data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (modelId && assessmentUuid) {
        try {
          // Fetch the right risks for this assessment type
          if (assessmentType === 'navigator') {
            await dispatch(fetchBuilderRisks({ assessmentUuid, modelId }));
          } else if (assessmentType === 'explorer') {
            await dispatch(fetchExplorerRisks({ assessmentUuid, modelId }));
          } else {
            await dispatch(fetchAssessmentRisks({ assessmentUuid, modelId }));
          }
          console.log('[RiskReviewStep] ✓ Fetched assessment risks for', assessmentType);

          // Then fetch risk card data, controls, and residual risk in parallel
          await Promise.all([
            dispatch(fetchRiskCardData({ assessmentUuid, modelId })),
            dispatch(setSavedControls(assessmentUuid, modelId)),
            dispatch(fetchSavedControlEvaluations(assessmentUuid, modelId)),
            dispatch(fetchResidualRisk(assessmentUuid, modelId))
          ]);
          console.log('[RiskReviewStep] ✓ Fetched all supporting data');
        } catch (error) {
          console.error('[RiskReviewStep] Error fetching data:', error);
        }
      }
    };

    fetchData();
  }, [modelId, assessmentUuid, assessmentType, dispatch]);

  // Set first risk as current when data loads
  useEffect(() => {
    if (allRisks.length > 0 && !currentRiskId) {
      const firstRisk = allRisks[0];
      selectRisk(firstRisk.riskId);
      dispatch(updateReviewProgress(modelId, 'risks', firstRisk.riskId));
    }
  }, [allRisks, currentRiskId, selectRisk, dispatch, modelId]);

  // Handle risk selection from navigator
  const handleRiskSelect = useCallback((risk) => {
    const index = allRisks.findIndex(r => r.riskId === risk.riskId);
    if (index >= 0) {
      setCurrentRiskIndex(index);
      selectRisk(risk.riskId);
      dispatch(updateReviewProgress(modelId, 'risks', risk.riskId));
    }
  }, [allRisks, selectRisk, dispatch, modelId]);

  // Navigate to next risk
  const handleNext = useCallback(() => {
    const nextIndex = currentRiskIndex + 1;
    if (nextIndex < allRisks.length) {
      setCurrentRiskIndex(nextIndex);
      const nextRisk = allRisks[nextIndex];
      selectRisk(nextRisk.riskId);
      dispatch(updateReviewProgress(modelId, 'risks', nextRisk.riskId));
    } else {
      // All risks reviewed — go to security (builder) or summary (navigator/explorer)
      goToStep(nextStepAfterRisks);
    }
  }, [currentRiskIndex, allRisks, selectRisk, dispatch, modelId, goToStep, nextStepAfterRisks]);

  // Navigate to previous risk
  const handlePrevious = useCallback(() => {
    const prevIndex = currentRiskIndex - 1;
    if (prevIndex >= 0) {
      setCurrentRiskIndex(prevIndex);
      const prevRisk = allRisks[prevIndex];
      selectRisk(prevRisk.riskId);
      dispatch(updateReviewProgress(modelId, 'risks', prevRisk.riskId));
    }
  }, [currentRiskIndex, allRisks, selectRisk, dispatch, modelId]);

  // Skip current risk
  const handleSkip = useCallback(() => {
    handleNext();
  }, [handleNext]);

  // Add manual risk handler
  const handleAddManualRisk = useCallback(async ({ areaKey, category, riskName }) => {
    try {
      // 1. Persist to database
      const response = await authFetch('/.netlify/functions/createRiskScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          riskArea: areaKey,
          category,
          riskName: riskName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create risk in database');
      }

      const data = await response.json();

      // 2. Update RTK state
      dispatch(addManualRisk({
        assessmentUuid,
        sectionName: areaKey,
        category,
        riskName: riskName.trim(),
      }));

      // 3. Auto-select the new risk (after state updates)
      if (data.riskId) {
        selectRisk(data.riskId);
      }

      // 4. Show success notification
      notifications.show({
        title: 'Risk Added',
        message: `"${riskName}" has been added to ${category}`,
        color: 'green'
      });
    } catch (error) {
      console.error('[RiskReviewStep] Error adding manual risk:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add risk',
        color: 'red'
      });
    }
  }, [modelId, assessmentUuid, dispatch, selectRisk]);

  // Validation
  if (allRisks.length === 0) {
    return (
      <Stack gap="lg">
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          No risks found for review. Complete the assessment first.
        </Alert>
        <Button onClick={() => goToStep('summary')}>
          Skip to Summary
        </Button>
      </Stack>
    );
  }

  if (!currentRisk) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        Loading risk data...
      </Alert>
    );
  }

  return (
    <Grid gutter="md">
      {/* Left sidebar - Risk Builder */}
      <Grid.Col span={{ base: 12, md: 3 }}>
        <RiskNavigator
          risksByArea={risksByArea}
          currentRisk={currentRisk}
          onRiskSelect={handleRiskSelect}
          templateData={templateData}
          onAddRisk={handleAddManualRisk}
        />
      </Grid.Col>

      {/* Right panel - Risk Canvas */}
      <Grid.Col span={{ base: 12, md: 9 }}>
        {/* Risk Canvas */}
        <RiskCanvas
          risk={currentRisk}
          currentRiskIndex={currentRiskIndex}
          totalRisks={allRisks.length}
          onNext={handleNext}
          onPrevious={currentRiskIndex > 0 ? handlePrevious : null}
          onSkip={handleSkip}
        />
      </Grid.Col>
    </Grid>
  );
}

export default RiskReviewStep;
